import { useMemo, useState } from "react";
import type { GfTabProps, GfView } from "../../../types/gifting";
import {
  CAMPAIGN_TYPE_LABEL,
  SOURCE_LABEL,
  STAGE_NAMES,
  buildBoardColumns,
  isArchived,
  listCandidates,
} from "../../../lib/giftingRules";
import { GfButton, GfSeg } from "../ui/GfUi";
import { CreatorFormModal } from "../modals/CreatorFormModal";
import { CampaignFormModal } from "../modals/CampaignFormModal";
import { AddEntryModal } from "../modals/AddEntryModal";
import { KanbanBoard } from "./pipeline/KanbanBoard";
import { ListView } from "./pipeline/ListView";
import { ArchiveView } from "./pipeline/ArchiveView";
import { buildPipelineCtx } from "./pipeline/model";

const AUTO_LOG_LIMIT = 4;

/** 案件（パイプライン）タブ。README「2. 案件」の構成順: 見出し → 自動更新バー → ビュー切替 → 案件チップ → 本体 */
export function PipelineTab(props: GfTabProps) {
  const { data, nowIso, nav } = props;
  const { board } = data;
  const [campaignOpen, setCampaignOpen] = useState(false);
  const [creatorOpen, setCreatorOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [addIds, setAddIds] = useState<string[]>([]);

  const ctx = useMemo(() => buildPipelineCtx(data, nowIso), [data, nowIso]);
  const live = useMemo(() => board.entries.filter((e) => !isArchived(e)), [board.entries]);
  const archived = useMemo(() => board.entries.filter((e) => isArchived(e)), [board.entries]);

  // 存在しない案件IDがURLに残っていたら「すべて」に倒す
  const filter =
    nav.state.campaign === "all" || board.campaigns.some((c) => c.id === nav.state.campaign) ? nav.state.campaign : "all";
  const filtered = useMemo(() => (filter === "all" ? live : live.filter((e) => e.campaign_id === filter)), [live, filter]);
  const columns = useMemo(
    () => buildBoardColumns(board.entries, ctx.campaigns, nowIso, filter),
    [board.entries, ctx.campaigns, nowIso, filter]
  );
  const listCount = useMemo(() => listCandidates(board.creators, board.entries).length, [board.creators, board.entries]);

  const view = nav.state.view;
  const selectedId = nav.state.sel;
  const openEntry = (id: string) => nav.openEntry(id, "deals");

  const chips = [
    { id: "all", name: "すべての案件", sub: `${live.length}名が進行中` },
    ...board.campaigns
      .filter((c) => c.status === "active" || live.some((e) => e.campaign_id === c.id))
      .map((c) => ({
        id: c.id,
        name: c.name,
        sub: `${CAMPAIGN_TYPE_LABEL[c.type]}・${live.filter((e) => e.campaign_id === c.id).length}名`,
      })),
  ];

  const autoEvents = board.recent_auto_events.filter((ev) => ev.source !== "manual");

  return (
    <>
      <div className="gf-page-head">
        <h1 className="gf-h1">案件管理</h1>
        <div className="gf-actions">
          <GfButton onClick={() => setCreatorOpen(true)}>クリエイターを追加</GfButton>
          <GfButton variant="primary" onClick={() => setCampaignOpen(true)}>
            新規案件
          </GfButton>
        </div>
      </div>

      <div className="gf-autobar">
        <span className="gf-dot" />
        <span className="gf-autobar-title">自動更新</span>
        <span className="gf-autobar-text">
          {autoEvents.length === 0
            ? "直近24時間の自動更新はありません。第1弾の自動遷移は本人フォーム送信のみ"
            : `直近24時間に ${autoEvents.length}件の段階が自動で更新されました。`}
        </span>
        {autoEvents.slice(0, AUTO_LOG_LIMIT).map((ev) => (
          <span className="gf-autobar-log" key={ev.id}>
            <span className="gf-mono">{SOURCE_LABEL[ev.source]}</span> → {ev.influencer_name}・{STAGE_NAMES[ev.to_stage] ?? ""}
          </span>
        ))}
        {autoEvents.length > AUTO_LOG_LIMIT && <span className="gf-autobar-log">他{autoEvents.length - AUTO_LOG_LIMIT}件</span>}
      </div>

      <div className="gf-viewrow">
        <GfSeg<GfView>
          ariaLabel="表示切替"
          value={view}
          onChange={(v) => nav.set({ view: v })}
          options={[
            { value: "kanban", label: "カンバン" },
            { value: "list", label: "一覧" },
            { value: "archive", label: `アーカイブ（${archived.length}）` },
          ]}
        />
        <span className="gf-viewrow-note">完了した案件は自動でアーカイブされます</span>
      </div>

      {view !== "archive" && (
        <div className="gf-dealchips">
          {chips.map((c) => (
            <button
              type="button"
              key={c.id}
              className={`gf-dealchip${filter === c.id ? " is-active" : ""}`}
              aria-pressed={filter === c.id}
              onClick={() => nav.set({ campaign: c.id })}
            >
              <span className="gf-dealchip-name">{c.name}</span>
              <span className="gf-dealchip-sub">{c.sub}</span>
            </button>
          ))}
        </div>
      )}

      {view === "kanban" && (
        <KanbanBoard
          columns={columns}
          listCount={listCount}
          ctx={ctx}
          selectedId={selectedId}
          onOpen={openEntry}
          onOpenSales={() => nav.set({ tab: "sales" })}
        />
      )}
      {view === "list" && <ListView entries={filtered} ctx={ctx} selectedId={selectedId} onOpen={openEntry} />}
      {view === "archive" && <ArchiveView entries={archived} ctx={ctx} board={board} selectedId={selectedId} onOpen={openEntry} />}

      <CreatorFormModal
        {...props}
        open={creatorOpen}
        onClose={() => setCreatorOpen(false)}
        onSaved={(influencerId, dealId) => {
          // 「Instagramから選ぶ」で案件まで追加済みなら、そのエントリーを開く
          if (dealId) {
            setCreatorOpen(false);
            nav.openEntry(dealId, "deals");
            return;
          }
          // 登録したクリエイターをそのまま案件に追加できるよう、追加ダイアログを続けて開く
          void props.refresh().then(() => {
            setCreatorOpen(false);
            setAddIds([influencerId]);
            setAddOpen(true);
          });
        }}
      />
      <CampaignFormModal
        {...props}
        open={campaignOpen}
        onClose={() => setCampaignOpen(false)}
        onSaved={(campaignId) => nav.set({ campaign: campaignId })}
      />
      <AddEntryModal
        {...props}
        open={addOpen}
        onClose={() => setAddOpen(false)}
        influencerIds={addIds}
        onSaved={() => undefined}
      />
    </>
  );
}
